/**
 * Cognito Hosted-UI social sign-in (Google / Apple / Facebook) with PKCE.
 *
 * ⚠️ This REPLACES a fundamentally different mechanism, not just a URL. The old flow used
 * the native provider SDKs (`SocialLogin.login`) to obtain a Google/Facebook token and
 * posted it to blaze. The kernel's `/auth/social/callback` does not accept provider tokens
 * at all — it takes a Cognito **authorization code** plus the PKCE **code_verifier**, and
 * exchanges them itself. So the shape of the flow changes:
 *
 *     openSocialLogin()  ->  system browser  ->  Cognito Hosted UI  ->  upstream provider
 *          ->  redirect to arcconnect://auth/callback?code=...&state=...
 *              (production native: https://arcconnect.abmrc.com/oauth/callback, see REDIRECT_URIS)
 *          ->  appUrlOpen (App.tsx)  ->  exchangeSocialCode()  ->  kernel  ->  tokens
 *
 * It is therefore ASYNCHRONOUS ACROSS A BROWSER ROUND-TRIP. Nothing can `await` a social
 * sign-in to completion any more; the session is established when the deep link arrives.
 *
 * Ported from the `cognito-hosted-ui` branch, which built this against this same kernel
 * endpoint. Two things were deliberately NOT ported: it posted to `apiClient` (blaze), and
 * it never stored the returned tokens — it relied on the web cookie transport, which leaves
 * native with a successful exchange and no session (D6).
 *
 * ── Why a custom scheme rather than an https App Link ────────────────────────────────────
 * `arcconnect://auth/callback` needs no certificate verification, so it works on debug and
 * release builds alike, and it is what every non-production native build uses. Production
 * native uses the https callback instead: the new prod host serves assetlinks.json and
 * apple-app-site-association, so the App/Universal Link can verify there (staging and dev
 * still do not serve them). RFC 8252 prefers claimed https URIs; PKCE is what makes the custom scheme safe
 * in the meantime, since an app that hijacks the scheme still cannot exchange the code
 * without the verifier.
 */
import { Capacitor } from "@capacitor/core";
import { Browser } from "@capacitor/browser";
import { Preferences } from "@capacitor/preferences";
import { CURRENT_ENV, kernelClient } from "./api";
import { AUTH_ENDPOINTS } from "./endpoints";

// Matches COGNITO_DOMAIN / COGNITO_CLIENT_ID in the kernel's environment. ⚠️ The client
// MUST be the same one the kernel exchanges with: PKCE binds the code to the client that
// requested it, so a mismatch fails at exchange time with an opaque invalid_grant.
//
// Picked per build environment, matching CURRENT_ENV in api.ts (decided at build time by
// REACT_APP_ENV). REACT_APP_COGNITO_DOMAIN / REACT_APP_COGNITO_CLIENT_ID
// override on any build. The app-client id is public (it is in every authorize URL), not a
// secret.
//   production: the prod pool (arc-infra envs/prod/outputs.env COGNITO_DOMAIN / COGNITO_CLIENT_ID).
//   staging / development / local / dev: the dev pool, which staging also used until it gets
//   its own.
// Same rule as CURRENT_ENV: anything that is not a known non-production env is production.
//
// ⚠️ Deliberately a comparison on the inlined REACT_APP_ENV literal rather than a map lookup:
// the minifier folds it, so a production bundle carries only the prod pool and a dev bundle
// only the dev pool.
// Parent (dev) branch: pinned to the DEV pool for every build, like the dev API URLs in api.ts.
// The production branches select the prod pool from REACT_APP_ENV as described above.
const COGNITO_FOR_ENV = {
  domain: "us-west-1jcxzlk28j.auth.us-west-1.amazoncognito.com",
  clientId: "5uf8feojt0mq20rfiloa3quebv",
};
const COGNITO_DOMAIN = process.env.REACT_APP_COGNITO_DOMAIN || COGNITO_FOR_ENV.domain;
const CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || COGNITO_FOR_ENV.clientId;

// Must match the callback URLs registered on the Cognito app client.
//
// ⚠️ WEB uses /oauth/callback, NOT /auth/callback. Under the single-origin gateway the
// browser app and blaze share one host, and blaze owns the whole `/auth/` prefix
// (/auth/refresh, /auth/social/, /auth/logout, /auth/registerHandsetUser/, ...). A web
// redirect to /auth/callback would be proxied to blaze and never reach the SPA. Native is
// unaffected: `arcconnect://auth/callback` is a custom scheme that never touches the
// gateway, so those two entries deliberately keep the old path.
//
// ⚠️ PRODUCTION NATIVE is the exception: it uses the https App/Universal Link
// `https://arcconnect.abmrc.com/oauth/callback`, not the custom scheme. Prod now serves
// /.well-known/assetlinks.json and apple-app-site-association (arc-infra nginx), the URL is
// registered on the prod Cognito client and in the kernel's ALLOWED_REDIRECT_URIS
// (arc-infra envs/prod/env.sh, app/kernel.env.template). When the link opens the app,
// App.tsx's generic appUrlOpen branch pushes /oauth/callback and OAuthCallback.tsx finishes
// the exchange. If the OS does not hand the redirect to the app, it lands on the web app
// instead. `arcconnect://auth/callback` is registered on prod too, as the fallback.
const REDIRECT_URIS: Record<string, string> = {
  production: "https://arcconnect.abmrc.com/oauth/callback",
  staging: "https://staging-arcconnect.abmrc.com/oauth/callback",
  development: "arcconnect://auth/callback",
  local: "arcconnect://auth/callback",
};

export const SOCIAL_REDIRECT_URI =
  Capacitor.getPlatform() === "web"
    ? `${window.location.origin}/oauth/callback`
    : REDIRECT_URIS[CURRENT_ENV] ?? REDIRECT_URIS.development;

/** Cognito's identity-provider names — NOT our internal lowercase provider codes. */
export type SocialProvider = "Google" | "SignInWithApple" | "Facebook";

const PROVIDER_BY_CODE: Record<string, SocialProvider> = {
  google: "Google",
  apple: "SignInWithApple",
  facebook: "Facebook",
};

const VERIFIER_KEY = "pkce_verifier";
const STATE_KEY = "oauth_state";
const PROVIDER_KEY = "oauth_provider";

const base64url = (bytes: Uint8Array): string =>
  btoa(String.fromCharCode(...Array.from(bytes)))
    .replace(/\+/g, "-")
    .replace(/\//g, "_")
    .replace(/=+$/, "");

/**
 * 32 random bytes as URL-safe base64 — 43 chars, exactly the RFC 7636 minimum.
 *
 * ⚠️ Uses `getRandomValues`, NOT `crypto.randomUUID`. `randomUUID` is restricted to secure
 * contexts and throws "crypto.randomUUID is not a function" on a plain-http origin — which
 * is what a LAN live-reload dev build is. `getRandomValues` carries no such restriction and
 * is the better primitive here anyway: UUIDs waste 4 bits per byte on hex plus version and
 * variant bits, so 32 raw bytes give more entropy in fewer characters.
 */
const randomUrlSafe = (bytes = 32): string => {
  const buf = new Uint8Array(bytes);
  crypto.getRandomValues(buf);
  return base64url(buf);
};

/** RFC 7636 PKCE pair, S256. */
export const generatePkce = async (): Promise<{ verifier: string; challenge: string }> => {
  // ⚠️ `crypto.subtle` is ALSO secure-context-only. We do not silently fall back to
  // `code_challenge_method=plain`: that would send the verifier in the clear in the
  // authorize URL and defeat the one protection that makes a custom-scheme redirect safe.
  // Better to fail loudly here than to ship a downgrade nobody notices.
  if (!crypto?.subtle?.digest) {
    throw new Error(
      "Secure crypto is unavailable in this WebView, so social sign-in cannot be started " +
        "safely. This happens on a plain-http origin; production serves https.",
    );
  }
  const verifier = randomUrlSafe();
  const hash = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(verifier));
  return { verifier, challenge: base64url(new Uint8Array(hash)) };
};

const buildAuthorizeUrl = (
  provider: SocialProvider,
  challenge: string,
  state: string,
): string => {
  const params = new URLSearchParams({
    response_type: "code",
    client_id: CLIENT_ID,
    redirect_uri: SOCIAL_REDIRECT_URI,
    scope: "openid email",
    identity_provider: provider,
    code_challenge: challenge,
    code_challenge_method: "S256",
    state,
    // Without this the browser silently reuses whatever provider session already exists,
    // which makes "sign in with a different account" impossible.
    prompt: "select_account",
  });
  return `https://${COGNITO_DOMAIN}/oauth2/authorize?${params}`;
};

/** True when a social sign-in is mid-flight, i.e. a callback is expected. */
export const hasSocialLoginInFlight = async (): Promise<boolean> => {
  const { value } = await Preferences.get({ key: VERIFIER_KEY });
  return !!value;
};

export const clearSocialLoginState = async (): Promise<void> => {
  await Promise.all([
    Preferences.remove({ key: VERIFIER_KEY }),
    Preferences.remove({ key: STATE_KEY }),
    Preferences.remove({ key: PROVIDER_KEY }),
  ]);
};

/**
 * Starts social sign-in. Returns as soon as the browser is open — it does NOT resolve to a
 * session. Completion arrives via the deep link; see `exchangeSocialCode`.
 */
export const openSocialLogin = async (providerCode: string): Promise<void> => {
  const provider = PROVIDER_BY_CODE[providerCode];
  if (!provider) {
    throw new Error(`Unsupported social provider: ${providerCode}`);
  }
  const { verifier, challenge } = await generatePkce();
  const state = randomUrlSafe(16);

  // Stored BEFORE the browser opens: the app may be evicted while backgrounded, and the
  // verifier is the only thing that can complete the exchange when it comes back.
  await Promise.all([
    Preferences.set({ key: VERIFIER_KEY, value: verifier }),
    Preferences.set({ key: STATE_KEY, value: state }),
    Preferences.set({ key: PROVIDER_KEY, value: providerCode }),
  ]);

  const url = buildAuthorizeUrl(provider, challenge, state);
  if (Capacitor.getPlatform() === "web") {
    // Navigate this tab so the callback lands in the same app instance.
    window.location.href = url;
  } else {
    await Browser.open({ url });
  }
};

export type SocialExchangeResult = {
  access_token: string;
  refresh_token?: string;
  new_user?: boolean;
  providerCode: string;
};

/**
 * Completes the flow: validates `state`, exchanges the code at the KERNEL, and returns the
 * tokens. Storing them and building the session is the caller's job (AuthContext), so that
 * social and OTP share one post-login path rather than forking it (B1).
 */
export const exchangeSocialCode = async (
  code: string,
  returnedState: string | null,
): Promise<SocialExchangeResult> => {
  const [storedState, verifier, storedProvider] = await Promise.all([
    Preferences.get({ key: STATE_KEY }),
    Preferences.get({ key: VERIFIER_KEY }),
    Preferences.get({ key: PROVIDER_KEY }),
  ]);

  // CSRF defence. A mismatch means this callback did not originate from our request, so the
  // code must not be exchanged.
  if (!storedState.value || returnedState !== storedState.value || !verifier.value) {
    await clearSocialLoginState();
    throw new Error("Sign-in could not be completed. Please try again.");
  }

  const providerCode = storedProvider.value ?? "google";

  // ⚠️ The kernel's terms gate applies to FIRST-TIME registrations only, but the client
  // cannot know which this is before the exchange. Send the device-local flag every time;
  // the kernel ignores it for returning users and raises TERMS_NOT_ACCEPTED for new ones.
  // Same `termsAgreed` key as the OTP path — NOT "termsAccepted" (bug #10).
  const termsAccepted = (await Preferences.get({ key: "termsAgreed" })).value === "true";

  try {
    const { data } = await kernelClient.post(AUTH_ENDPOINTS.KERNEL_SOCIAL_CALLBACK, {
      code,
      code_verifier: verifier.value,
      redirect_uri: SOCIAL_REDIRECT_URI,
      provider: PROVIDER_BY_CODE[providerCode],
      terms_accepted: termsAccepted,
    });
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      new_user: data.new_user,
      providerCode,
    };
  } finally {
    // One code, one attempt: an authorization code is single-use, so keeping the verifier
    // around only invites a second exchange that fails confusingly.
    await clearSocialLoginState();
  }
};
